import { Component } from './component.js';

/**
 * Sticky Add to Cart Component
 * 
 * Shows sticky ATC bar when original buy buttons are scrolled out of view
 */
export class StickyATCComponent extends Component {
  /**
   * @type {Element | null}
   */
  originalBuyButtons = null;

  /**
   * @type {IntersectionObserver | null}
   */
  intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    // Find the original buy buttons in product-information section
    this.originalBuyButtons = document.querySelector('.product-information .product-form-buttons, .product-information [data-type="add-to-cart-form"]');
    
    if (!this.originalBuyButtons) {
      return;
    }

    this.setupIntersectionObserver();
    this.setupEventListeners();
  }

  setupScrollHandler() {
    // Add scroll handler for more reliable detection
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateStickyVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  updateStickyVisibility() {
    if (!this.originalBuyButtons) return;

    const rect = this.originalBuyButtons.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    const isBelowATC = rect.bottom < 0;

    if (isVisible) {
      this.hide();
    } else if (isBelowATC) {
      this.show();
    } else {
      this.hide();
    }
  }

  setupIntersectionObserver() {
    if (!this.originalBuyButtons) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.hide();
        } else {
          // Only show if user scrolled past the buy buttons (not above them)
          const rect = this.originalBuyButtons.getBoundingClientRect();
          if (rect.bottom < 0) {
            this.show();
          } else {
            this.hide();
          }
        }
      }
    }, options);

    this.intersectionObserver.observe(this.originalBuyButtons);
  }

  setupEventListeners() {
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
    document.addEventListener('plan-change', this.handlePlanPriceChange.bind(this));

    document.addEventListener('cart:item-added', this.handleCartSuccess.bind(this));
  }

  /**
   * Handle scroll to top for select options buttons
   */
  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Handle variant change events to sync the sticky form
   * @param {CustomEvent & {detail: {variant: any}}} event 
   */
  handleVariantChange(event) {
    if (!event.detail || !event.detail.variant) return;

    const variant = event.detail.variant;
    
    // Update hidden variant inputs in sticky forms
    const variantInputs = this.querySelectorAll('input[name="id"]');
    variantInputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        input.value = variant.id;
      }
    });

    this.updatePriceDisplay(variant);
    
    this.updateButtonState(variant);

    this.updateProductImage(variant);
  }

  /**
   * Handle subscription plan change to update sticky price
   * @param {CustomEvent & {detail: {planType: string, yearlyTotal?: string, monthlyPrice?: string}}} event
   */
  handlePlanPriceChange(event) {
    const { planType, yearlyTotal, yearlyPerItem, monthlyPrice } = event.detail || {};
    if (!planType) return;

    const rawPrice = planType === 'yearly'
      ? (yearlyPerItem ?? yearlyTotal)
      : monthlyPrice;
    const priceCents = parseInt(rawPrice, 10);
    if (!priceCents) return;

    const priceContainers = this.querySelectorAll('[ref="priceContainer"], .sticky-atc__price');
    const formattedPrice = this.formatMoney(priceCents);

    priceContainers.forEach(container => {
      const priceSpan = container.querySelector('.price') || container;
      if (priceSpan) {
        priceSpan.textContent = formattedPrice;
      }
    });
  }

  /**
   * Update price display in sticky bar
   * @param {any} variant 
   */
  updatePriceDisplay(variant) {
    const priceContainers = this.querySelectorAll('[ref="priceContainer"], .sticky-atc__price');
    priceContainers.forEach(container => {
      const priceSpan = container.querySelector('.price');
      if (priceSpan && variant.price !== undefined) {
        // Format price using Shopify's money format
        const formattedPrice = this.formatMoney(variant.price);
        priceSpan.textContent = formattedPrice;
      }

      // Update compare at price if exists
      const compareAtPriceSpan = container.querySelector('.compare-at-price');
      if (compareAtPriceSpan && variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        compareAtPriceSpan.textContent = formattedComparePrice;
        if (compareAtPriceSpan instanceof HTMLElement) {
          compareAtPriceSpan.style.display = 'inline';
        }
      } else if (compareAtPriceSpan instanceof HTMLElement) {
        compareAtPriceSpan.style.display = 'none';
      }
    });
  }

  /**
   * Update button state based on variant availability
   * @param {any} variant 
   */
  updateButtonState(variant) {
    const addToCartButtons = this.querySelectorAll('button[type="submit"], .sticky-atc__atc-button');
    addToCartButtons.forEach(button => {
      if (button instanceof HTMLButtonElement) {
        if (variant.available) {
          button.disabled = false;
          const addToCartText = button.dataset.addToCartText || 'Add to cart';
          button.querySelector('.add-to-cart-text__content')?.textContent?.replace(/.*/, addToCartText);
        } else {
          button.disabled = true;
          const soldOutText = button.dataset.soldOutText || 'Sold out';
          button.querySelector('.add-to-cart-text__content')?.textContent?.replace(/.*/, soldOutText);
        }
      }
    });
  }

  /**
   * Update product image when variant changes
   * @param {any} variant 
   */
  updateProductImage(variant) {
    const imageContainer = this.querySelector('.sticky-atc__image');
    if (!imageContainer) return;

    const img = imageContainer.querySelector('img');
    if (!img) return;

    if (variant.featured_media && variant.featured_media.preview_image) {
      const newSrc = variant.featured_media.preview_image.src.replace(/\.(jpg|jpeg|png|gif|webp).*/, '_80x80.$1');
      img.src = newSrc;
      img.alt = variant.featured_media.alt || '';
    }
  }

  /**
   * Handle successful cart addition
   */
  handleCartSuccess() {
    this.hide();
  }

  /**
   * Check if we are scrolled below the original ATC buttons
   * @returns {boolean}
   */
  isBelowOriginalATC() {
    if (!this.originalBuyButtons) return false;
    
    const rect = this.originalBuyButtons.getBoundingClientRect();
    // We are below the ATC if the top of the element is above the viewport
    return rect.bottom < 0;
  }

  /**
   * Check if original buy buttons are visible in viewport
   * @returns {boolean}
   */
  isOriginalVisible() {
    if (!this.originalBuyButtons) return false;
    
    const rect = this.originalBuyButtons.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    return rect.top >= 0 && rect.bottom <= windowHeight;
  }

  /**
   * Show sticky ATC bar
   */
  show() {
    this.classList.add('is-visible');
  }

  /**
   * Hide sticky ATC bar
   */
  hide() {
    this.classList.remove('is-visible');
  }

  /**
   * Format money using Shopify's format or fallback
   * @param {number} cents 
   * @returns {string}
   */
  formatMoney(cents) {
    // @ts-ignore - Shopify global may not be typed
    if (typeof window.Shopify?.formatMoney === 'function') {
      // @ts-ignore
      return window.Shopify.formatMoney(cents);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return `$${dollars}`;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}

// Register the custom element
if (customElements && !customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyATCComponent);
}